import { Component, computed, inject, output, OutputEmitterRef, Signal, signal, WritableSignal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { AiAssistShellComponent } from '../../../../shared/components/ai-assist-shell/ai-assist-shell.component';
import {
  EAiAssistSource,
  IAiAssistGenerateRequest,
} from '../../../../shared/components/ai-assist-shell/ai-assist-shell.interface';
import { AiReportCommentButtonComponent } from '../../../../shared/components/ai-report-comment-button/ai-report-comment-button.component';
import { AiReportViewComponent } from '../../../../shared/components/ai-report-view/ai-report-view.component';
import { IAiReport } from '../../../../shared/components/ai-report-view/ai-report-view.interface';
import { ISegmentedButtonOption } from '../../../../shared/components/segmented-button/segmented-button.interface';
import { UserEffects } from '../../../../state/user/user.effects';
import { AI_ASSIST_ACTIONS_REQUIRING_DESCRIPTION, AI_ASSIST_ITEM_MODES } from './ai-assist-dialog.constants';
import { IAiAssistDialogData, IAiAssistSuggestion, TAiAssistResult } from './ai-assist-dialog.interface';
import { AiAssistDialogService } from './ai-assist-dialog.service';
import { AiAssistDialogStore } from './ai-assist-dialog.store';
import { aiAssistTextToHtml } from './ai-assist-dialog.utilities';

/**
 * The item modal's AI dialog, in two modes for a saved item (a new item only has the first):
 * - edit: a title and/or description from the item draft; "Apply" closes with it (`TAiAssistResult`) and the modal
 *   puts it in the form, where it is saved, or not, like any other edit;
 * - report: a report on the saved item, to read; "Add as comment" saves it as the user's comment
 *   (`reportCommentAdded`) and closes with nothing.
 * The request form, the mode switch, the AI animation and "Try again" come from `app-ai-assist-shell`.
 */
@Component({
  imports: [
    AiAssistShellComponent,
    AiReportCommentButtonComponent,
    AiReportViewComponent,
    MatButton,
    MatDialogClose,
    MatIcon,
    TranslateModule,
  ],
  providers: [AiAssistDialogService, AiAssistDialogStore],
  selector: 'app-ai-assist-dialog',
  styleUrl: './ai-assist-dialog.component.scss',
  templateUrl: './ai-assist-dialog.component.html',
})
export class AiAssistDialogComponent {
  public static readonly dialogParameters = {
    autoFocus: 'first-tabbable',
    maxWidth: '100%',
    restoreFocus: false,
    width: EDialogWidth.small,
  };

  private readonly data: IAiAssistDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef: MatDialogRef<AiAssistDialogComponent, TAiAssistResult> = inject(MatDialogRef);
  private readonly store = inject(AiAssistDialogStore);

  /** The report reads the saved item, so a new one has no report mode and no switch. */
  public readonly modes: ISegmentedButtonOption<EAiAssistSource>[] =
    this.data.itemId === null ? [] : AI_ASSIST_ITEM_MODES;
  /** Null for a new item, which has no report mode. */
  public readonly itemId: number | null = this.data.itemId;
  /** The item modal refreshes its comment list. */
  public readonly reportCommentAdded: OutputEmitterRef<void> = output<void>();
  public readonly source: WritableSignal<EAiAssistSource> = signal(EAiAssistSource.itemModal);
  public readonly disabledActions: Signal<readonly string[]> = computed(() =>
    this.source() === EAiAssistSource.itemModal && !this.data.hasDescription
      ? AI_ASSIST_ACTIONS_REQUIRING_DESCRIPTION
      : [],
  );
  /** No title and no description: the shell then asks the user to write the request themselves. */
  public readonly isSourceEmpty: Signal<boolean> = computed(
    () =>
      this.source() === EAiAssistSource.itemModal && !this.data.hasDescription && !this.data.item.title?.trim().length,
  );
  /** A linked item saves an empty draft from needing a request of its own, from context level "standard" on. */
  public readonly hasLinkedContext: Signal<boolean> = computed(
    () => this.source() === EAiAssistSource.itemModal && this.data.hasRelatedItems,
  );
  public readonly isLoading: Signal<boolean> = this.store.isLoading;
  public readonly suggestion: Signal<IAiAssistSuggestion | null> = this.store.suggestion;
  public readonly report: Signal<IAiReport | null> = this.store.report;
  public readonly hasResult: Signal<boolean> = computed(() => this.suggestion() !== null || this.report() !== null);

  public onSourceChange(source: EAiAssistSource): void {
    this.source.set(source);
  }

  public onGenerate({ action, contextLevel, prompt }: IAiAssistGenerateRequest): void {
    if (this.source() === EAiAssistSource.itemReport && this.data.itemId !== null) {
      this.store.requestReport({
        itemId: this.data.itemId,
        request: { action, contextLevel, prompt },
      });

      return;
    }

    this.store.requestSuggestion({
      // The shell hands back a key of this source's quick actions.
      action,
      boardId: this.data.boardId,
      contextLevel,
      item: this.data.item,
      itemId: this.data.itemId,
      prompt,
      timeZone: UserEffects.getUser().configuration.timezone,
    });
  }

  /** Back to the request, with the action and prompt kept, to ask again. */
  public onTryAgain(): void {
    this.store.clearResult();
  }

  public onApply(): void {
    const suggestion: IAiAssistSuggestion | null = this.suggestion();

    if (!suggestion) {
      return;
    }

    this.dialogRef.close({
      description: suggestion.description ? aiAssistTextToHtml(suggestion.description) : null,
      title: suggestion.title,
    });
  }
}
